import React, { useState, useEffect, useRef } from "react";
import { Container, Card, Row, Col, Table, Badge, Button, Modal, Form, Spinner, InputGroup } from "react-bootstrap";
import { FaHeadset, FaPlus, FaTicketAlt, FaSearch, FaPaperPlane, FaClock, FaCheckCircle, FaExclamationTriangle, FaUser, FaBoxes, FaPaperclip, FaTrash, FaTimes, FaEye } from "react-icons/fa";
import { getFirestore, collection, query, where, addDoc, updateDoc, doc, orderBy, serverTimestamp, onSnapshot, arrayUnion, setDoc } from "firebase/firestore";
import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage";
import firebaseApp from "../../firebase/credenciales";
import Swal from "sweetalert2";
import sendCustomEmail from "../../functions/sendCustomEmail";
// Atribución del consumo de Firestore: los onSnapshot no pasan por el wrapper
// instrumentado, así que se informa de la primera carga de esta pantalla.
import { establecerOrigenConsumo, registrarLecturas } from "../../functions/consumoLecturas";

const db = getFirestore(firebaseApp);
const storage = getStorage(firebaseApp);

function GestionMesaAyuda({ usuario }) {
  const [loading, setLoading] = useState(false);
  const [tickets, setTickets] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [newMessage, setNewMessage] = useState("");
  const [sendingMessage, setSendingMessage] = useState(false);
  const chatEndRef = useRef(null);
  // Marca la primera carga del listener de tickets (para atribuir la lectura).
  const primeraCargaRef = useRef(true);

  const [formData, setFormData] = useState({
    subject: "",
    category: "Soporte Técnico",
    priority: "Media",
    description: "",
    email: "" // Correo del cliente para notificaciones
  });

  // Nuevos estados para archivos y visualización de imágenes
  const [ticketFiles, setTicketFiles] = useState([]);
  const [chatFile, setChatFile] = useState(null);
  const [chatFilePreview, setChatFilePreview] = useState("");
  const [zoomImage, setZoomImage] = useState(null);

  // Estados para la resolución del ticket
  const [showResolveModal, setShowResolveModal] = useState(false);
  const [resolutionText, setResolutionText] = useState("");
  const [resolvingTicket, setResolvingTicket] = useState(false);

  const ruc = usuario?.ruc;
  const isAdmin = ["admin", "superAdmin"].includes(usuario?.rol || "");

  const [searchTerm, setSearchTerm] = useState("");

  // Pre-llenar email del usuario en el formulario
  useEffect(() => {
    if (usuario?.email) {
      setFormData(prev => ({ ...prev, email: usuario.email }));
    }
  }, [usuario]);

  // Cargar tickets en tiempo real
  useEffect(() => {
    if (!ruc) return;

    // Marca el origen de las lecturas de esta pantalla para el seguimiento
    // de consumo por usuario (visible solo para superAdmin).
    establecerOrigenConsumo("mesa-ayuda:listado");

    const workerId = usuario.dni || usuario.uid;
    let q;

    if (isAdmin) {
      if (ruc === "20609212102") {
        // La empresa de soporte técnico ve todos los tickets del sistema
        q = query(
          collection(db, "help_tickets"),
          orderBy("created_at", "desc")
        );
      } else {
        // Administradores de otras empresas ven solo los suyos
        q = query(
          collection(db, "help_tickets"),
          where("ruc_empresa", "==", ruc),
          orderBy("created_at", "desc")
        );
      }
    } else {
      // Colaboradores comunes ven solo sus propios tickets
      q = query(
        collection(db, "help_tickets"),
        where("ruc_empresa", "==", ruc),
        where("worker_id", "==", workerId),
        orderBy("created_at", "desc")
      );
    }

    const unsubscribe = onSnapshot(q, (snapshot) => {
      // Solo se reporta la primera carga del listener: las siguientes son
      // actualizaciones en tiempo real de los mismos documentos.
      if (primeraCargaRef.current) {
        primeraCargaRef.current = false;
        registrarLecturas(
          { help_tickets: snapshot.size || 0 },
          { origen: "mesa-ayuda:listado" }
        );
      }
      const data = [];
      snapshot.forEach((doc) => data.push({ id: doc.id, ...doc.data() }));
      setTickets(data);
    });

    return () => unsubscribe();
  }, [ruc, isAdmin, usuario]);

  // Actualizar ticket seleccionado en tiempo real si cambia en la base de datos
  useEffect(() => {
    if (selectedTicket) {
      const updated = tickets.find(t => t.id === selectedTicket.id);
      if (updated) {
        setSelectedTicket(updated);
      }
    }
  }, [tickets, selectedTicket?.id]);

  // Scroll al final del chat
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [selectedTicket?.messages]);

  const filteredTickets = tickets.filter(t => {
    const term = searchTerm.toLowerCase();
    return (
      (t.subject || "").toLowerCase().includes(term) ||
      (t.worker_name || "").toLowerCase().includes(term) ||
      (t.category || "").toLowerCase().includes(term) ||
      (t.status || "").toLowerCase().includes(term)
    );
  });

  // Controladores de pegar (Paste) para capturas de pantalla
  const handlePaste = (e, target) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    for (let i = 0; i < items.length; i++) {
      if (items[i].type.indexOf("image") !== -1) {
        const file = items[i].getAsFile();
        const uniqueName = `screenshot_${Date.now()}_${Math.random().toString(36).substr(2, 5)}.png`;
        const renamedFile = new File([file], uniqueName, { type: file.type });
        if (target === "ticket") {
          setTicketFiles(prev => [...prev, renamedFile]);
        } else if (target === "chat") {
          setChatFile(renamedFile);
          const reader = new FileReader();
          reader.onloadend = () => {
            setChatFilePreview(reader.result);
          };
          reader.readAsDataURL(renamedFile);
        }
        e.preventDefault();
      }
    }
  };

  const handleTicketFileChange = (e) => {
    if (e.target.files) {
      const filesArray = Array.from(e.target.files);
      setTicketFiles(prev => [...prev, ...filesArray]);
    }
  };

  const removeTicketFile = (index) => {
    setTicketFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleChatFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setChatFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setChatFilePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeChatFile = () => {
    setChatFile(null);
    setChatFilePreview("");
  };

  const handleCreateTicket = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);

      const ticketCollection = collection(db, "help_tickets");
      const newDocRef = doc(ticketCollection);
      const ticketId = newDocRef.id;

      const uploadedUrls = [];
      for (const file of ticketFiles) {
        const storageRef = ref(storage, `help_tickets/${ticketId}/${file.name}`);
        const snapshot = await uploadBytes(storageRef, file);
        const downloadUrl = await getDownloadURL(snapshot.ref);
        uploadedUrls.push({
          url: downloadUrl,
          name: file.name
        });
      }

      const ticketData = {
        ...formData,
        ruc_empresa: ruc,
        worker_id: usuario.dni || usuario.uid,
        worker_name: `${usuario.nombre || ""} ${usuario.apellidos || ""}`.trim(),
        status: "Abierto",
        created_at: serverTimestamp(),
        updated_at: serverTimestamp(),
        attachments: uploadedUrls,
        messages: [
          {
            sender_id: usuario.dni || usuario.uid,
            sender_name: usuario.nombre || (isAdmin ? "Soporte" : "Usuario"),
            text: formData.description,
            timestamp: new Date().toISOString(),
            is_admin: isAdmin,
            attachments: uploadedUrls
          }
        ]
      };

      await setDoc(newDocRef, ticketData);

      // Enviar correo de confirmación de creación al cliente
      try {
        const emailCollection = usuario?.datosEmpresa?.coleccionEmail || "mail";
        const subjectEmail = `Ticket Registrado con Éxito: ${formData.subject}`;
        const bodyEmail = `Hola,\n\nHemos recibido tu reporte en la Mesa de Ayuda:\n\n` +
                          `• Asunto: ${formData.subject}\n` +
                          `• Categoría: ${formData.category}\n` +
                          `• Prioridad: ${formData.priority}\n\n` +
                          `Detalle del reporte:\n${formData.description}\n\n` +
                          `Estaremos trabajando en tu solicitud y te mantendremos informado por este medio.`;
        
        await sendCustomEmail(formData.email, null, null, subjectEmail, bodyEmail, emailCollection);
      } catch (errEmail) {
        console.error("Error al enviar correo de creación:", errEmail);
      }

      setShowCreateModal(false);
      setFormData({ subject: "", category: "Soporte Técnico", priority: "Media", description: "", email: "" });
      setTicketFiles([]);
      Swal.fire("Éxito", "Ticket creado correctamente y correo de confirmación enviado", "success");
    } catch (error) {
      console.error("Error al crear ticket:", error);
      Swal.fire("Error", "No se pudo crear el ticket", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleSendMessage = async () => {
    if ((!newMessage.trim() && !chatFile) || !selectedTicket) return;

    try {
      setSendingMessage(true);
      const ticketRef = doc(db, "help_tickets", selectedTicket.id);

      const messageAttachments = [];
      if (chatFile) {
        const storageRef = ref(storage, `help_tickets/${selectedTicket.id}/messages/${chatFile.name}`);
        const snapshot = await uploadBytes(storageRef, chatFile);
        const downloadUrl = await getDownloadURL(snapshot.ref);
        messageAttachments.push({
          url: downloadUrl,
          name: chatFile.name
        });
      }

      const messageObj = {
        sender_id: usuario.dni || usuario.uid,
        sender_name: usuario.nombre || (isAdmin ? "Soporte" : "Usuario"),
        text: newMessage,
        timestamp: new Date().toISOString(),
        is_admin: isAdmin,
        attachments: messageAttachments
      };

      const nextStatus = selectedTicket.status === "Resuelto" || selectedTicket.status === "Cerrado"
        ? (isAdmin ? "En Proceso" : "Abierto")
        : (isAdmin ? "En Proceso" : selectedTicket.status);

      await updateDoc(ticketRef, {
        messages: arrayUnion(messageObj),
        updated_at: serverTimestamp(),
        status: nextStatus
      });

      setNewMessage("");
      setChatFile(null);
      setChatFilePreview("");
    } catch (error) {
      console.error("Error al enviar mensaje:", error);
    } finally {
      setSendingMessage(false);
    }
  };

  const handleResolveTicket = async (e) => {
    e.preventDefault();
    if (!resolutionText.trim() || !selectedTicket) return;

    try {
      setResolvingTicket(true);
      const ticketRef = doc(db, "help_tickets", selectedTicket.id);

      const messageObj = {
        sender_id: usuario.dni || usuario.uid,
        sender_name: "Sistema / Soporte",
        text: `✅ TICKET SOLUCIONADO:\n${resolutionText}`,
        timestamp: new Date().toISOString(),
        is_admin: true
      };

      await updateDoc(ticketRef, {
        messages: arrayUnion(messageObj),
        updated_at: serverTimestamp(),
        status: "Resuelto",
        resolution: resolutionText
      });

      // Enviar correo de solución al cliente
      if (selectedTicket.email) {
        try {
          const emailCollection = usuario?.datosEmpresa?.coleccionEmail || "mail";
          const subjectEmail = `Ticket Solucionado: ${selectedTicket.subject}`;
          const bodyEmail = `Hola,\n\nTu ticket "${selectedTicket.subject}" ha sido marcado como SOLUCIONADO.\n\n` +
                            `Detalles de la solución / acciones realizadas:\n` +
                            `${resolutionText}\n\n` +
                            `Gracias por tu comunicación.`;
          
          await sendCustomEmail(selectedTicket.email, null, null, subjectEmail, bodyEmail, emailCollection);
        } catch (errEmail) {
          console.error("Error al enviar correo de resolución:", errEmail);
        }
      }

      setShowResolveModal(false);
      setResolutionText("");
      Swal.fire("Éxito", "El ticket ha sido solucionado correctamente y se notificó al cliente", "success");
    } catch (error) {
      console.error("Error al resolver ticket:", error);
      Swal.fire("Error", "No se pudo marcar el ticket como solucionado", "error");
    } finally {
      setResolvingTicket(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "Abierto": return <Badge bg="primary">Abierto</Badge>;
      case "En Proceso": return <Badge bg="warning" text="dark">En Proceso</Badge>;
      case "Resuelto": return <Badge bg="success">Resuelto</Badge>;
      case "Cerrado": return <Badge bg="secondary">Cerrado</Badge>;
      default: return <Badge bg="light" text="dark">{status}</Badge>;
    }
  };

  const getPriorityBadge = (priority) => {
    switch (priority) {
        case "Baja": return <Badge bg="info" className="opacity-75">Baja</Badge>;
        case "Media": return <Badge bg="primary" className="opacity-75">Media</Badge>;
        case "Alta": return <Badge bg="warning" text="dark">Alta</Badge>;
        case "Urgente": return <Badge bg="danger">Urgente</Badge>;
        default: return <Badge bg="light" text="dark">{priority}</Badge>;
    }
  };

  const stats = {
    total: tickets.length,
    abiertos: tickets.filter(t => t.status === "Abierto").length,
    enProceso: tickets.filter(t => t.status === "En Proceso").length,
    resueltos: tickets.filter(t => t.status === "Resuelto").length
  };

  return (
    <div
      className="min-vh-100"
      style={{
        background: "linear-gradient(135deg, #ffffff 0%, #ffffff 100%)",
        padding: "20px 0",
      }}
    >
      <Container fluid className="px-lg-5 px-3 pb-5">
        <Card
          className="shadow-lg border-0"
          style={{ borderRadius: "15px", overflow: "hidden", minHeight: "85vh" }}
        >
          {/* Header Premium Estilo CatalogoEPPs (Morado) */}
          <Card.Header
            className="text-white py-4"
            style={{
              background: "linear-gradient(135deg, #7c3aed 0%, #a855f7 100%)",
              borderBottom: "3px solid #6d28d9",
            }}
          >
            <div className="text-center">
              <h2 className="fw-bold mb-1 d-flex align-items-center justify-content-center">
                <FaHeadset className="me-3" />
                Mesa de Ayuda
              </h2>
              <p className="mb-0 opacity-75">
                Centro de soporte y gestión de incidencias internas
              </p>
            </div>
          </Card.Header>

          <Card.Body className="p-4" style={{ backgroundColor: "#f8f9fa" }}>
            {/* Barra superior con acciones */}
            <Card className="shadow-sm border-0 mb-4" style={{ borderRadius: "15px" }}>
              <Card.Body className="p-4">
                <Row className="align-items-center">
                  <Col md={1}>
                    <div
                      className="rounded-3 p-3 text-center"
                      style={{
                        background: "linear-gradient(135deg, #7c3aed 0%, #a855f7 100%)",
                        color: "white",
                      }}
                    >
                      <FaHeadset size={24} />
                    </div>
                  </Col>
                  <Col md={7}>
                    <h5 className="mb-1 fw-semibold">Acciones de Soporte</h5>
                    <p className="text-muted mb-0 small">
                      Gestione solicitudes de ayuda y resuelva dudas de sus colaboradores
                    </p>
                  </Col>
                  <Col md={4} className="text-end">
                    <Button
                      variant="light"
                      onClick={() => setShowCreateModal(true)}
                      className="d-flex align-items-center gap-2 ms-auto"
                      style={{ borderRadius: "10px", boxShadow: "0 2px 4px rgba(0,0,0,0.1)" }}
                    >
                      <FaPlus size="16px" /> Nuevo Ticket
                    </Button>
                  </Col>
                </Row>
              </Card.Body>
            </Card>

            {/* Fila de KPIs */}
            <Row className="mb-4 g-3">
              <Col md={3}>
                <Card className="border-0 shadow-sm" style={{ borderRadius: "15px" }}>
                  <Card.Body className="text-center" style={{ background: "linear-gradient(135deg, #7c3aed 0%, #a855f7 100%)", color: "white", borderRadius: "15px" }}>
                    <h4 className="fw-bold mb-1">{stats.total}</h4>
                    <p className="mb-0 opacity-90 small">Total Tickets</p>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={3}>
                <Card className="border-0 shadow-sm" style={{ borderRadius: "15px" }}>
                  <Card.Body className="text-center" style={{ background: "linear-gradient(135deg, #dc2626 0%, #ef4444 100%)", color: "white", borderRadius: "15px" }}>
                    <h4 className="fw-bold mb-1">{stats.abiertos}</h4>
                    <p className="mb-0 opacity-90 small">Abiertos</p>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={3}>
                <Card className="border-0 shadow-sm" style={{ borderRadius: "15px" }}>
                  <Card.Body className="text-center" style={{ background: "linear-gradient(135deg, #f59e0b 0%, #fbbf24 100%)", color: "white", borderRadius: "15px" }}>
                    <h4 className="fw-bold mb-1">{stats.enProceso}</h4>
                    <p className="mb-0 opacity-90 small">En Proceso</p>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={3}>
                <Card className="border-0 shadow-sm" style={{ borderRadius: "15px" }}>
                  <Card.Body className="text-center" style={{ background: "linear-gradient(135deg, #059669 0%, #10b981 100%)", color: "white", borderRadius: "15px" }}>
                    <h4 className="fw-bold mb-1">{stats.resueltos}</h4>
                    <p className="mb-0 opacity-90 small">Resueltos</p>
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            <Row className="g-4">
              {/* Ticket List Section */}
              <Col lg={selectedTicket ? 4 : 12}>
                <Card className="border-0 shadow-sm rounded-4 overflow-hidden h-100">
                  <Card.Header className="text-white py-3 border-0" style={{ background: "linear-gradient(135deg, #7c3aed 0%, #a855f7 100%)" }}>
                    <div className="d-flex align-items-center">
                      <FaTicketAlt className="me-2" />
                      <h6 className="mb-0 fw-semibold">Mis Solicitudes</h6>
                    </div>
                  </Card.Header>
                  <Card.Body className="p-0">
                    <div className="bg-light p-2">
                       <InputGroup className="bg-white rounded-pill border shadow-sm px-2">
                          <InputGroup.Text className="bg-transparent border-0"><FaSearch className="text-muted" /></InputGroup.Text>
                          <Form.Control 
                            placeholder="Buscar tickets..." 
                            className="bg-transparent border-0 shadow-none py-2" 
                            style={{ fontSize: "0.9rem" }} 
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                          />
                       </InputGroup>
                    </div>
                    <div style={{ maxHeight: "55vh", overflowY: "auto" }}>
                       <Table hover className="mb-0 align-middle">
                        <tbody>
                          {loading ? (
                            <tr><td className="text-center py-5"><Spinner animation="border" variant="primary" /></td></tr>
                          ) : filteredTickets.length === 0 ? (
                            <tr><td className="text-center py-5 text-muted small">No hay tickets registrados que coincidan con su búsqueda.</td></tr>
                          ) : (
                            filteredTickets.map((t) => (
                              <tr key={t.id} onClick={() => setSelectedTicket(t)} style={{ cursor: "pointer", backgroundColor: selectedTicket?.id === t.id ? "#f3f0ff" : "transparent" }}>
                                <td className="p-3">
                                  <div className="d-flex justify-content-between align-items-start mb-1">
                                    <span className="fw-bold text-dark" style={{ fontSize: "0.9rem" }}>{t.subject}</span>
                                    {getStatusBadge(t.status)}
                                  </div>
                                  <div className="d-flex justify-content-between align-items-center mt-2">
                                    <small className="text-muted d-flex align-items-center" style={{ fontSize: "0.75rem" }}>
                                        <FaUser className="me-1" size={10} /> {t.worker_name}
                                    </small>
                                    {getPriorityBadge(t.priority)}
                                  </div>
                                  <div className="mt-2 d-flex justify-content-between align-items-center">
                                    <small className="text-muted" style={{ fontSize: "0.7rem" }}>
                                        {t.created_at?.toDate ? t.created_at.toDate().toLocaleString() : "..."}
                                    </small>
                                    <Badge bg="secondary" className="opacity-50" style={{ fontSize: "0.6rem" }}>{t.category}</Badge>
                                  </div>
                                </td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </Table>
                    </div>
                  </Card.Body>
                </Card>
              </Col>

              {/* Chat Thread Section */}
              {selectedTicket && (
                <Col lg={8}>
                  <Card className="border-0 shadow-sm rounded-4 h-100 d-flex flex-column overflow-hidden">
                    <Card.Header className="text-white py-3 border-0 d-flex justify-content-between align-items-center" style={{ background: "linear-gradient(135deg, #7c3aed 0%, #a855f7 100%)" }}>
                        <div className="d-flex align-items-center">
                            <div className="bg-white bg-opacity-20 rounded-circle p-2 me-3 d-flex align-items-center justify-content-center">
                                <FaUser size={16} />
                            </div>
                            <div>
                                <h6 className="mb-0 fw-bold">{selectedTicket.subject}</h6>
                                <small className="opacity-75 d-block" style={{ fontSize: "0.8rem" }}>
                                  Prioridad: {selectedTicket.priority} | Cliente: {selectedTicket.email || "Sin correo"}
                                </small>
                            </div>
                        </div>
                        <div className="d-flex gap-2 align-items-center">
                            {selectedTicket.status !== "Resuelto" && selectedTicket.status !== "Cerrado" && (
                              <Button 
                                variant="success" 
                                size="sm" 
                                className="fw-semibold px-3 border-0" 
                                style={{ borderRadius: "8px", backgroundColor: "#10b981" }}
                                onClick={() => setShowResolveModal(true)}
                              >
                                Resolver
                              </Button>
                            )}
                            <Button variant="outline-light" size="sm" className="rounded-circle border-0 d-flex align-items-center justify-content-center" onClick={() => setSelectedTicket(null)} style={{ backgroundColor: "rgba(255,255,255,0.1)", width: "30px", height: "30px" }}>
                                &times;
                            </Button>
                        </div>
                    </Card.Header>
                    
                    <Card.Body 
                      className="p-0 flex-grow-1 d-flex flex-column" 
                      style={{ maxHeight: "550px", minHeight: "550px", backgroundColor: "#fdfdfd" }}
                      onDragOver={(e) => e.preventDefault()}
                      onDrop={(e) => {
                        e.preventDefault();
                        if (e.dataTransfer.files && e.dataTransfer.files[0]) {
                          const file = e.dataTransfer.files[0];
                          if (file.type.startsWith("image/")) {
                            setChatFile(file);
                            const reader = new FileReader();
                            reader.onloadend = () => {
                              setChatFilePreview(reader.result);
                            };
                            reader.readAsDataURL(file);
                          }
                        }
                      }}
                    >
                      <div className="p-4 overflow-auto flex-grow-1" id="chat-container">
                        {selectedTicket.messages?.map((msg, idx) => {
                          const isMyMessage = msg.sender_id === (usuario.dni || usuario.uid);
                          return (
                            <div key={idx} className={`d-flex mb-3 ${isMyMessage ? "justify-content-end" : "justify-content-start"}`}>
                              <div className={`p-3 rounded-4 shadow-sm`} style={{ 
                                maxWidth: "75%", 
                                backgroundColor: isMyMessage ? "#7c3aed" : "#ffffff",
                                color: isMyMessage ? "white" : "#333",
                                borderBottomRightRadius: isMyMessage ? "4px" : "1.5rem",
                                borderBottomLeftRadius: isMyMessage ? "1.5rem" : "4px",
                                border: isMyMessage ? "none" : "1px solid #e9ecef"
                              }}>
                                <div className="fw-bold small mb-1" style={{ fontSize: "0.75rem", opacity: 0.9 }}>
                                  {msg.sender_name} {msg.is_admin && !isMyMessage && <Badge bg="light" text="dark" className="ms-1 px-2 py-0.5 border" style={{ fontSize: "0.6rem" }}>Soporte</Badge>}
                                </div>
                                <div style={{ fontSize: "0.9rem", whiteSpace: "pre-wrap" }}>{msg.text}</div>
                                
                                {/* Adjuntos en el mensaje */}
                                {msg.attachments && msg.attachments.length > 0 && (
                                  <div className="mt-2 d-flex flex-wrap gap-2">
                                    {msg.attachments.map((att, attIdx) => (
                                      <div 
                                        key={attIdx} 
                                        className="rounded-3 overflow-hidden border cursor-pointer bg-black bg-opacity-10 position-relative" 
                                        style={{ width: "150px", height: "110px" }}
                                        onClick={() => setZoomImage(att.url)}
                                      >
                                        <img src={att.url} alt={att.name} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                                        <div className="position-absolute bottom-0 start-0 end-0 bg-dark bg-opacity-50 text-white text-center py-1 small" style={{ fontSize: "0.65rem" }}>
                                          <FaEye className="me-1" size={10} /> Ampliar
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                )}

                                <div className="text-end mt-1" style={{ fontSize: "0.7rem", opacity: 0.7 }}>
                                  {new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                        <div ref={chatEndRef} />
                      </div>

                      <div className="p-3 border-top bg-white">
                        {chatFilePreview && (
                          <div className="position-relative d-inline-block border rounded-3 p-1 bg-light mb-2 shadow-sm" style={{ width: "100px", height: "100px" }}>
                            <img src={chatFilePreview} alt="preview" className="w-100 h-100 object-fit-cover rounded-2" />
                            <Button 
                              variant="danger" 
                              size="sm" 
                              className="position-absolute top-0 end-0 p-0 rounded-circle d-flex align-items-center justify-content-center" 
                              style={{ width: "20px", height: "20px", marginTop: "-6px", marginRight: "-6px" }}
                              onClick={removeChatFile}
                            >
                              <FaTimes size={10} />
                            </Button>
                          </div>
                        )}
                        <Form onSubmit={(e) => { e.preventDefault(); handleSendMessage(); }}>
                          <InputGroup className="bg-light rounded-pill border-0 px-2 shadow-sm align-items-center">
                            <Button 
                              variant="link" 
                              className="text-muted p-0 px-2 d-flex align-items-center justify-content-center"
                              onClick={() => document.getElementById("chat-file-input").click()}
                              style={{ height: "38px" }}
                            >
                              <FaPaperclip size={18} />
                            </Button>
                            <input 
                              id="chat-file-input"
                              type="file" 
                              accept="image/*" 
                              style={{ display: "none" }} 
                              onChange={handleChatFileChange}
                            />
                            <Form.Control 
                              as="textarea"
                              rows={1}
                              placeholder="Escribe su respuesta aquí... (O pega captura)" 
                              className="border-0 bg-transparent shadow-none px-3 py-2" 
                              style={{ resize: "none", fontSize: "0.9rem" }}
                              value={newMessage}
                              onChange={(e) => setNewMessage(e.target.value)}
                              onKeyDown={(e) => { if(e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSendMessage(); } }}
                              onPaste={(e) => handlePaste(e, "chat")}
                            />
                            <Button 
                              variant="link" 
                              className="text-primary d-flex align-items-center justify-content-center p-0 px-3"
                              onClick={handleSendMessage}
                              disabled={sendingMessage || (!newMessage.trim() && !chatFile)}
                              style={{ color: "#7c3aed" }}
                            >
                              {sendingMessage ? <Spinner animation="border" size="sm" /> : <FaPaperPlane size={18} style={{ color: "#7c3aed" }} />}
                            </Button>
                          </InputGroup>
                        </Form>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              )}
            </Row>
          </Card.Body>
        </Card>
      </Container>

      {/* Modal Ticket (Estilo CatalogoEPPs) */}
      <Modal show={showCreateModal} onHide={() => setShowCreateModal(false)} centered size="lg">
        <Modal.Header closeButton className="border-0">
            <Modal.Title className="fw-bold d-flex align-items-center">
                <div className="rounded-circle p-2 me-3 d-flex align-items-center justify-content-center" style={{ background: "rgba(124, 58, 237, 0.1)", color: "#7c3aed" }}>
                    <FaTicketAlt size={20} />
                </div>
                Nuevo Ticket de Ayuda
            </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleCreateTicket} onPaste={(e) => handlePaste(e, "ticket")}>
          <Modal.Body className="p-4">
            <Row className="g-3">
              <Col md={12}>
                <Form.Group>
                  <Form.Label className="small fw-bold">Asunto del Ticket</Form.Label>
                  <Form.Control 
                    required 
                    placeholder="Resumen corto de su necesidad..." 
                    className="form-control-lg border-2"
                    style={{ borderRadius: "10px" }}
                    value={formData.subject}
                    onChange={(e) => setFormData({...formData, subject: e.target.value})}
                  />
                </Form.Group>
              </Col>
              <Col md={12}>
                <Form.Group>
                  <Form.Label className="small fw-bold">Correo Electrónico (Cliente / Notificaciones)</Form.Label>
                  <Form.Control 
                    type="email"
                    required 
                    placeholder="correo@cliente.com" 
                    className="form-control-lg border-2"
                    style={{ borderRadius: "10px" }}
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label className="small fw-bold">Categoría</Form.Label>
                  <Form.Select 
                    className="form-control-lg border-2"
                    style={{ borderRadius: "10px" }}
                    value={formData.category}
                    onChange={(e) => setFormData({...formData, category: e.target.value})}
                  >
                    <option>Soporte Técnico</option>
                    <option>Recursos Humanos</option>
                    <option>Planillas</option>
                    <option>Otros</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label className="small fw-bold">Prioridad Estimada</Form.Label>
                  <Form.Select 
                    className="form-control-lg border-2"
                    style={{ borderRadius: "10px" }}
                    value={formData.priority}
                    onChange={(e) => setFormData({...formData, priority: e.target.value})}
                  >
                    <option>Baja</option>
                    <option>Media</option>
                    <option>Alta</option>
                    <option>Urgente</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={12}>
                <Form.Group>
                  <Form.Label className="small fw-bold">Descripción / Detalle</Form.Label>
                  <Form.Control 
                    as="textarea" 
                    rows={4} 
                    required 
                    placeholder="Describa a detalle su requerimiento..." 
                    className="border-2"
                    style={{ borderRadius: "10px", resize: "none" }}
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                  />
                </Form.Group>
              </Col>
              
              {/* Sección de adjuntar capturas / imágenes */}
              <Col md={12}>
                <Form.Group 
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={(e) => {
                    e.preventDefault();
                    if (e.dataTransfer.files) {
                      setTicketFiles(prev => [...prev, ...Array.from(e.dataTransfer.files)]);
                    }
                  }}
                >
                  <Form.Label className="small fw-bold">Capturas de Pantalla / Adjuntos</Form.Label>
                  <div 
                    className="p-3 text-center bg-light rounded-3 d-flex flex-column align-items-center justify-content-center" 
                    style={{ border: "2px dashed #7c3aed", cursor: "pointer", backgroundColor: "#fbfaff" }}
                    onClick={() => document.getElementById("ticket-file-input").click()}
                  >
                    <FaPaperclip className="mb-2 text-primary text-opacity-75" size={24} style={{ color: "#7c3aed" }} />
                    <span className="text-muted small fw-semibold">Arrastra archivos aquí o haz clic para seleccionar</span>
                    <span className="text-muted mt-1" style={{ fontSize: "0.75rem" }}>Puedes pegar imágenes directamente usando Ctrl+V o Cmd+V</span>
                    <input 
                      id="ticket-file-input"
                      type="file" 
                      multiple 
                      accept="image/*" 
                      style={{ display: "none" }} 
                      onChange={handleTicketFileChange}
                    />
                  </div>
                  {ticketFiles.length > 0 && (
                    <div className="d-flex flex-wrap gap-2 mt-3">
                      {ticketFiles.map((file, idx) => {
                        const objectUrl = URL.createObjectURL(file);
                        return (
                          <div key={idx} className="position-relative border rounded-3 p-1 bg-white shadow-sm" style={{ width: "80px", height: "80px" }}>
                            <img src={objectUrl} alt="preview" className="w-100 h-100 object-fit-cover rounded-2" />
                            <Button 
                              variant="danger" 
                              size="sm" 
                              className="position-absolute top-0 end-0 p-0 rounded-circle d-flex align-items-center justify-content-center" 
                              style={{ width: "20px", height: "20px", marginTop: "-5px", marginRight: "-5px" }}
                              onClick={(e) => { e.stopPropagation(); removeTicketFile(idx); }}
                            >
                              <FaTimes size={10} />
                            </Button>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </Form.Group>
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer className="border-0">
            <Button variant="light" className="px-4 py-2" onClick={() => setShowCreateModal(false)}>Cerrar</Button>
            <Button 
                variant="primary" 
                type="submit" 
                className="px-5 py-2 fw-bold" 
                style={{ background: "#7c3aed", borderColor: "#7c3aed", borderRadius: "10px" }}
                disabled={loading}
            >
              {loading ? <Spinner animation="border" size="sm" /> : "Generar Ticket"}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Modal Zoom/Lightbox para imágenes adjuntas */}
      <Modal show={!!zoomImage} onHide={() => setZoomImage(null)} centered size="xl" contentClassName="bg-transparent border-0">
        <Modal.Body className="p-0 text-center position-relative">
          <Button 
            variant="dark" 
            className="position-absolute top-0 end-0 m-3 rounded-circle d-flex align-items-center justify-content-center bg-opacity-70 border-0" 
            style={{ width: "40px", height: "40px", zIndex: 1050 }}
            onClick={() => setZoomImage(null)}
          >
            <FaTimes size={20} />
          </Button>
          {zoomImage && (
            <img 
              src={zoomImage} 
              alt="Adjunto ampliado" 
              className="img-fluid rounded-3 shadow-lg" 
              style={{ maxHeight: "85vh", maxWidth: "100%", objectFit: "contain" }} 
            />
          )}
        </Modal.Body>
      </Modal>

      {/* Modal para Resolver Ticket */}
      <Modal show={showResolveModal} onHide={() => setShowResolveModal(false)} centered>
        <Modal.Header closeButton className="border-0">
          <Modal.Title className="fw-bold">Resolver Ticket</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleResolveTicket}>
          <Modal.Body className="p-4">
            <Form.Group>
              <Form.Label className="small fw-bold">Detalle de la Solución / Acciones Realizadas</Form.Label>
              <Form.Control 
                as="textarea"
                rows={5}
                required
                placeholder="Describe a detalle las acciones realizadas para solucionar la incidencia..."
                style={{ borderRadius: "10px", resize: "none" }}
                value={resolutionText}
                onChange={(e) => setResolutionText(e.target.value)}
              />
              <Form.Text className="text-muted small mt-2 d-block">
                * Este detalle se guardará en el historial del ticket y se enviará por correo al cliente.
              </Form.Text>
            </Form.Group>
          </Modal.Body>
          <Modal.Footer className="border-0">
            <Button variant="light" className="px-4 py-2" onClick={() => setShowResolveModal(false)}>Cerrar</Button>
            <Button 
                variant="success" 
                type="submit" 
                className="px-5 py-2 fw-bold" 
                style={{ borderRadius: "10px" }}
                disabled={resolvingTicket}
            >
              {resolvingTicket ? <Spinner animation="border" size="sm" /> : "Confirmar Solución"}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  );
}

export default GestionMesaAyuda;
